// Moved from `src/hooks/mutation/useMutateInviteUsersToOrgTeam.ts` (now the
// app delegate) with the OT4 mutation slice (ARCFE-1033).
//
// Severed per ARCFE-921: the destructive toast read the backend `detail.msg`
// off an AxiosError. The `onInviteError` port fires inside the same
// transport-error branch with the raw error; the delegate owns the extraction
// AND the intl copy. `isTransportError` replaces `instanceof AxiosError` —
// equivalent (it reads axios's own marker) and duplicate-copy-proof.
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  GET_ORGANIZATION_MEMBERS,
  type InviteUsersToOrgTeamRequest,
} from '../../contracts';
import { InviteUsersToTeams } from '../api/organization';
import { isTransportError, type WorkspaceHttpError } from '../config';

export interface InviteUsersToOrgTeamVariables {
  organizationId: string;
  input: InviteUsersToOrgTeamRequest;
}

export interface UseMutateInviteUsersToOrgTeamOptions {
  /** App-reaction port: fired where the legacy destructive toast fired. */
  onInviteError?: (error: WorkspaceHttpError) => void;
}

/** POST /organization/{organization_id}/teams/{team_id}/invitations */
export function useMutateInviteUsersToOrgTeam(
  options?: UseMutateInviteUsersToOrgTeamOptions
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, input }: InviteUsersToOrgTeamVariables) => {
      if (!organizationId) {
        throw new Error('Organization ID is required');
      }

      if (!input.team_id) {
        throw new Error('Team ID is required');
      }

      return await InviteUsersToTeams(organizationId, input);
    },
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [GET_ORGANIZATION_MEMBERS, variables.organizationId],
      });
    },
    onError: (error) => {
      if (isTransportError(error)) {
        options?.onInviteError?.(error);
      }
    },
  });
}
